import React, { useEffect, useMemo, useState } from "react";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Fade from "@mui/material/Fade";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import type { WorkflowGuidanceCardConfig } from "./types";

const resolveAnchor = (selectors: string[]): HTMLElement | null => {
    if (typeof document === "undefined") return null;
    for (const selector of selectors) {
        const next = document.querySelector(selector);
        if (next instanceof HTMLElement) {
            return next;
        }
    }
    return null;
};

type WorkflowGuidanceCardProps = {
    card: WorkflowGuidanceCardConfig | null;
    onDismiss: (cardId: string) => void;
};

const WorkflowGuidanceCard: React.FC<WorkflowGuidanceCardProps> = ({
    card,
    onDismiss,
}) => {
    const selectorKey = useMemo(
        () => (card?.targetSelectors ?? []).join("|"),
        [card?.targetSelectors],
    );
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    useEffect(() => {
        if (!card) {
            setAnchorEl(null);
            return;
        }

        const updateAnchor = () => {
            setAnchorEl((prev) => {
                const next = resolveAnchor(card.targetSelectors);
                return prev === next ? prev : next;
            });
        };

        updateAnchor();
        const intervalId = window.setInterval(updateAnchor, 250);
        window.addEventListener("resize", updateAnchor);
        window.addEventListener("scroll", updateAnchor, true);

        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener("resize", updateAnchor);
            window.removeEventListener("scroll", updateAnchor, true);
        };
    }, [card, selectorKey]);

    if (!card || !anchorEl) return null;

    return (
        <Popper
            open
            anchorEl={anchorEl}
            placement={card.placement ?? "right-start"}
            transition
            modifiers={[
                {
                    name: "offset",
                    options: {
                        offset: [0, 12],
                    },
                },
                {
                    name: "preventOverflow",
                    options: {
                        padding: 16,
                    },
                },
                {
                    name: "flip",
                    options: {
                        padding: 16,
                    },
                },
            ]}
            sx={{ zIndex: 1600 }}
        >
            {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={140}>
                    <Paper
                        elevation={0}
                        sx={{
                            width: "min(380px, calc(100vw - 32px))",
                            borderRadius: "18px",
                            border: "1px solid rgba(148, 163, 184, 0.28)",
                            backgroundColor: "#0F172A",
                            boxShadow: "0px 24px 56px rgba(2, 6, 23, 0.45)",
                            p: 2.5,
                            fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                gap: 1,
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontSize: "18px",
                                        lineHeight: "24px",
                                        fontWeight: 700,
                                        letterSpacing: "-0.01em",
                                        color: "#F8FAFC",
                                    }}
                                >
                                    {card.title}
                                </Typography>
                            </Box>
                            <IconButton
                                size="small"
                                onClick={() => onDismiss(card.id)}
                                sx={{
                                    p: 0.25,
                                    mt: "-2px",
                                    mr: "-2px",
                                    color: "#94A3B8",
                                }}
                                aria-label="Dismiss guidance"
                            >
                                <CloseRoundedIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Box>

                        <Typography
                            sx={{
                                mt: 1.25,
                                fontSize: "15px",
                                lineHeight: "23px",
                                color: "#CBD5E1",
                            }}
                        >
                            {card.body}
                        </Typography>

                        {card.secondaryText ? (
                            <Typography
                                sx={{
                                    mt: 1.25,
                                    fontSize: "12px",
                                    lineHeight: "18px",
                                    fontWeight: 600,
                                    color: "#94A3B8",
                                }}
                            >
                                {card.secondaryText}
                            </Typography>
                        ) : null}

                        <Box
                            sx={{
                                mt: 2.25,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                gap: 1,
                            }}
                        >
                            <Button
                                size="medium"
                                variant={card.primaryAction ? "outlined" : "contained"}
                                onClick={() => onDismiss(card.id)}
                                sx={{
                                    textTransform: "none",
                                    borderRadius: "11px",
                                    minHeight: "40px",
                                    px: 2,
                                    fontSize: "13px",
                                    lineHeight: "19px",
                                    fontWeight: 600,
                                    boxShadow: "none",
                                    borderColor: card.primaryAction
                                        ? "rgba(147, 197, 253, 0.7)"
                                        : "transparent",
                                    backgroundColor: card.primaryAction
                                        ? "rgba(59, 130, 246, 0.18)"
                                        : "#2563EB",
                                    color: card.primaryAction ? "#DBEAFE" : "#F8FAFC",
                                    "&:hover": {
                                        borderColor: card.primaryAction
                                            ? "rgba(147, 197, 253, 0.82)"
                                            : "transparent",
                                        backgroundColor: card.primaryAction
                                            ? "rgba(59, 130, 246, 0.26)"
                                            : "#1D4ED8",
                                        boxShadow: "none",
                                    },
                                }}
                            >
                                Got it
                            </Button>
                            {card.primaryAction ? (
                                <Button
                                    size="medium"
                                    variant="contained"
                                    onClick={card.primaryAction.onClick}
                                    sx={{
                                        textTransform: "none",
                                        borderRadius: "11px",
                                        minHeight: "40px",
                                        px: 2,
                                        fontSize: "13px",
                                        lineHeight: "19px",
                                        fontWeight: 600,
                                        color: "#F8FAFC",
                                        backgroundColor: "#2563EB",
                                        boxShadow: "none",
                                        "&:hover": {
                                            backgroundColor: "#1D4ED8",
                                            boxShadow: "none",
                                        },
                                    }}
                                >
                                    {card.primaryAction.label}
                                </Button>
                            ) : null}
                        </Box>
                    </Paper>
                </Fade>
            )}
        </Popper>
    );
};

export default WorkflowGuidanceCard;
